import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Pencil, Loader2, BookOpen, GraduationCap, X, Plus } from "lucide-react";
import { toast } from "sonner";

export default function TeacherAssignmentsPage() {
  const [teachers, setTeachers] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [classes, setClasses] = useState([]);
  const [editing, setEditing] = useState(null); // teacher object
  const [form, setForm] = useState({ class_ids: [], subjects: [] });
  const [newSubject, setNewSubject] = useState("");
  const [newClass, setNewClass] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [t, a, c] = await Promise.allSettled([
      api.get("/users", { params: { role: "teacher" } }),
      api.get("/erp/teacher-assignments"),
      api.get("/classes"),
    ]);
    if (t.status === "fulfilled") setTeachers(t.value.data);
    else toast.error("Impossible de charger les enseignants");
    if (a.status === "fulfilled") {
      const map = {};
      a.value.data.forEach(r => { map[r.teacher_id] = r; });
      setAssignments(map);
    }
    if (c.status === "fulfilled") setClasses(c.value.data.map(cl => cl.name).filter(Boolean));
  };
  useEffect(() => { load(); }, []);

  const openEdit = (t) => {
    const a = assignments[t.id] || { class_ids: [], subjects: [] };
    setForm({ class_ids: [...(a.class_ids || [])], subjects: [...(a.subjects || [])] });
    setNewSubject(""); setNewClass("");
    setEditing(t);
  };

  const toggleClass = (name) => {
    setForm(f => ({
      ...f,
      class_ids: f.class_ids.includes(name) ? f.class_ids.filter(c => c !== name) : [...f.class_ids, name],
    }));
  };

  const addSubject = () => {
    const s = newSubject.trim();
    if (!s || form.subjects.includes(s)) return;
    setForm(f => ({ ...f, subjects: [...f.subjects, s] }));
    setNewSubject("");
  };

  const addCustomClass = () => {
    const c = newClass.trim();
    if (!c || form.class_ids.includes(c)) return;
    setForm(f => ({ ...f, class_ids: [...f.class_ids, c] }));
    setNewClass("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/erp/teacher-assignments", {
        teacher_id: editing.id,
        class_ids: form.class_ids,
        subjects: form.subjects,
      });
      toast.success("Affectation enregistrée");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const classOptions = Array.from(new Set([...classes, ...form.class_ids]));

  return (
    <div className="space-y-6" data-testid="teacher-assignments-page">
      <div>
        <div className="chip mb-2"><Users className="h-3 w-3" /><span>AFFECTATIONS ENSEIGNANTS</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Affectations classes & matières</h1>
        <p className="text-slate-500 mt-1">Définissez les classes et matières de chaque enseignant. Leur espace (cours, devoirs, élèves) sera limité à ce périmètre.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Enseignant</th>
                <th className="px-4 py-3 text-left">Classes affectées</th>
                <th className="px-4 py-3 text-left">Matières</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {teachers.map(t => {
                const a = assignments[t.id];
                return (
                  <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ta-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#1E3A8A]">{t.full_name || t.email}</div>
                      <div className="text-xs text-slate-400">{t.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      {a?.class_ids?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {a.class_ids.map(c => <span key={c} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-[#1E3A8A] font-semibold"><GraduationCap className="h-3 w-3" />{c}</span>)}
                        </div>
                      ) : <span className="text-xs text-orange-600 font-semibold">Aucune — accès restreint</span>}
                    </td>
                    <td className="px-4 py-3">
                      {a?.subjects?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {a.subjects.map(s => <span key={s} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-50 text-[#166534] font-semibold"><BookOpen className="h-3 w-3" />{s}</span>)}
                        </div>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => openEdit(t)} className="gap-1" data-testid={`ta-edit-${t.id}`}>
                        <Pencil className="h-3 w-3" />Affecter
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {teachers.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-400">Aucun enseignant enregistré.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Affectation — {editing?.full_name || editing?.email}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Classes</Label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-3">
                {classOptions.map(name => (
                  <label key={name} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.class_ids.includes(name)} onCheckedChange={() => toggleClass(name)} data-testid={`ta-class-${name}`} />
                    <span>{name}</span>
                  </label>
                ))}
                {classOptions.length === 0 && <div className="text-xs text-slate-400 col-span-2">Aucune classe configurée — ajoutez-en une ci-dessous.</div>}
              </div>
              <div className="flex gap-2 mt-2">
                <Input placeholder="Autre classe (ex : CM1 B)" value={newClass} onChange={e => setNewClass(e.target.value)} onKeyDown={e => e.key === "Enter" && addCustomClass()} data-testid="ta-new-class" />
                <Button variant="outline" size="icon" onClick={addCustomClass} data-testid="ta-add-class"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Matières</Label>
              <div className="flex flex-wrap gap-1 mb-2 min-h-6">
                {form.subjects.map(s => (
                  <span key={s} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 text-[#166534] font-semibold">
                    {s}
                    <button onClick={() => setForm(f => ({ ...f, subjects: f.subjects.filter(x => x !== s) }))} data-testid={`ta-del-subject-${s}`}><X className="h-3 w-3" /></button>
                  </span>
                ))}
                {form.subjects.length === 0 && <span className="text-xs text-slate-400">Aucune matière</span>}
              </div>
              <div className="flex gap-2">
                <Input placeholder="Ex : Mathématiques" value={newSubject} onChange={e => setNewSubject(e.target.value)} onKeyDown={e => e.key === "Enter" && addSubject()} data-testid="ta-new-subject" />
                <Button variant="outline" size="icon" onClick={addSubject} data-testid="ta-add-subject"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="ta-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
